import type {
    ComorbiditeitenBehandelingenOpties,
    ComorbiditeitenMedicatieOpties,
    DiabetesMellitusOpties,
    LeverziektenOpties,
    MaligniteitenOpties,
} from '@common/customControls/bariatrics';
import type { NullablePrimitives } from '@common/utils/utilityTypes';

export enum VitaminedeficientiesOpties {
    ja = 'Ja',
    nee = 'Nee',
    onbekend = 'Onbekend',
}

export enum ConsistentieOntlastingOpties {
    brijig = 'Brijig',
    dun = 'Dun',
    met_slijm = 'Met slijm',
    met_zichtbaar_bloed = 'Met zichtbaar bloed',
    obstipatie = 'Obstipatie',
    overig = 'Overig',
    vast = 'Vast',
    waterdun = 'Waterdun',
}
export enum OntlastingspatroonOpties {
    anders = 'Anders',
    dagelijks = 'Dagelijks',
}
export enum OperatieTypeWensOpties {
    anders = 'Anders',
    gastric_bypass = 'Gastric bypass',
    gastric_sleeve = 'Gastric sleeve',
    keuzehulp_nog_invullen = 'Moet keuzehulp nog invullen',
    mini_gastric_bypass = 'Mini-gastric bypass',
}
export enum PatientIsVerwezenDoorOpties {
    huisarts = 'Huisarts',
    medisch_specialist = 'Medisch specialist',
}
export enum PatientIsMobielOpties {
    beperkt = 'Beperkt',
    ja = 'Ja',
    nee = 'Nee',
}

export type Anamnese = NullablePrimitives<{
    consult_internist_gehad: boolean;
    consult_internist_toelichting: string;
    consult_MDL_arts_gehad: boolean;
    consult_MDL_arts_toelichting: string;
    eerdere_operaties: boolean;
    eerdere_operaties_toelichting: string;
    helicobacter_pylori_behandeld_met_antibiotica: boolean;
    helicobacter_pylori_behandeld_met_antibiotica_type: string;
    helicobacter_pylori_onderzoek_datum: string;
    helicobacter_pylori_onderzoek_gehad: boolean;
    helicobacter_pylori_sprake_van: boolean;
    medische_voorgeschiedenis: string;
    vitaminedeficienties: VitaminedeficientiesOpties;
    vitaminedeficienties_toelichting: string;
}>;

export type SpecieleAnamnese = NullablePrimitives<{
    buikklachten: boolean;
    buikklachten_toelichting: string;
    consistentie_ontlasting: ConsistentieOntlastingOpties;
    consistentie_ontlasting_overig: string;
    duur_overgewicht_in_jaren: number;
    motivatie_behandeling: string;
    ontlasting_frequentie_per_week: number;
    ontlastingspatroon: OntlastingspatroonOpties;
    patient_is_verwezen_door: PatientIsVerwezenDoorOpties;
    toelichting_speciele_anamnese: string;
}>;

export type Medicatie = NullablePrimitives<{
    allergieen: string;
    medicatie: boolean;
    medicatie_toelichting: string;
}>;

export type FamiliaireBelasting = NullablePrimitives<{
    diabetes_mellitus: boolean;
    hart_en_vaatziekten: boolean;
    hartfalen: boolean;
    hypertensie: boolean;
    overgewicht: boolean;
    overgewicht_bij_wie_options: {
        overgewicht_broer_zus?: boolean | null;
        overgewicht_moeder?: boolean | null;
        overgewicht_vader?: boolean | null;
    };
}>;

export type LichamelijkOnderzoek = NullablePrimitives<{
    abdominaal: string;
    extremiteiten: string;
    littekens_abdomen: boolean;
    littekens_toelichting: string;
    patient_is_mobiel: PatientIsMobielOpties;
    smetten: boolean;
    viscerale_obesitas: boolean;
}>;

export type Comorbiditeiten = NullablePrimitives<{
    bindweefselziekte: boolean;
    cerebrovasculaire_aandoening: boolean;
    chronische_longziekte: boolean;
    congestief_hartfalen: boolean;
    dementie: boolean;
    diabetes_mellitus_details: DiabetesMellitusOpties;
    diabetes_mellitus_diagnose: ComorbiditeitenMedicatieOpties;
    dyslipidemie: ComorbiditeitenMedicatieOpties;
    gastrointestinaal_ulcuslijden: boolean;
    GERD: ComorbiditeitenMedicatieOpties;
    gewrichtsklachten: ComorbiditeitenBehandelingenOpties;
    hba1c: number;
    HDL_cholesterol: number;
    HIV_AIDS: boolean;
    hypertensie_diagnose: ComorbiditeitenMedicatieOpties;
    klachten_van_reflux: boolean;
    LDL_cholesterol: number;
    leverziekte: LeverziektenOpties;
    maligniteit: MaligniteitenOpties;
    myocardinfarct: boolean;
    nierziekte: boolean;
    OSAS: ComorbiditeitenBehandelingenOpties;
    para_hemiplegie: boolean;
    perifeer_vaatlijden_aneurysma_aorta: boolean;
    triglyceriden: number;
    trombose: boolean;
    verhoogd_risico_DVT_of_PE: boolean;
}>;

export type AanvullendeOnderzoeken = NullablePrimitives<{
    ander_onderzoek: boolean;
    ander_onderzoek_toelichting: string;
    cardiologisch_onderzoek: boolean;
    cardiologisch_onderzoek_toelichting: string;
    longfunctieonderzoek: boolean;
    longfunctieonderzoek_toelichting: string;
    onderzoek_interne_geneeskunde: boolean;
    onderzoek_interne_geneeskunde_toelichting: string;
    slaaponderzoek: boolean;
    slaaponderzoek_toelichting: string;
}>;

export type Conclusie = NullablePrimitives<{
    contra_indicatie: boolean;
    contra_indicatie_toelichting: string;
    dagbehandeling: boolean;
    dagbehandeling_toelichting: string;
    wens_voor_type_operatie: OperatieTypeWensOpties;
    wens_voor_type_operatie_anders_toelichting: string;
}>;

export type BariPreNurseSpecialistMedScreeningReportBaseData = Anamnese &
    SpecieleAnamnese &
    Medicatie &
    FamiliaireBelasting &
    LichamelijkOnderzoek &
    AanvullendeOnderzoeken &
    Conclusie;

export type BariPreNurseSpecialistMedScreeningReportReadData = BariPreNurseSpecialistMedScreeningReportBaseData & {
    aanvullende_onderzoeken: string;
    conclusie: string;
    overgewicht_broer_zus: boolean;
    overgewicht_moeder: boolean;
    overgewicht_vader: boolean;
};
